// Display (accessibility) settings — text size + high-contrast mode.
//
// Standard we implement: WCAG 2.1 AA. The high-contrast theme darkens the
// gray text ramp to ≥4.5:1 (SC 1.4.3); the text-size lever scales the root
// font-size, which every rem-based Chakra token follows (SC 1.4.4).
//
// Persistence is PER DEVICE (localStorage), not per hotel: display comfort
// belongs to the person/monitor at the desk, unlike Settings → Languages
// which is hotel policy and lives server-side.
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { makeTheme } from '../theme';

const SCALE_KEY = 'display.textScale';
const CONTRAST_KEY = 'display.highContrast';

// Root font-size per level. Chakra sizes tokens in rem, so this scales
// (nearly) all text AND spacing proportionally — xs 12→13.5→15px,
// sm 14→15.75→17.5px, md 16→18→20px.
export const TEXT_SCALES = {
  md: '100%',
  lg: '112.5%',
  xl: '125%',
};

export function readStoredScale() {
  const v = localStorage.getItem(SCALE_KEY);
  return TEXT_SCALES[v] ? v : 'md';
}

export function readStoredContrast() {
  const v = localStorage.getItem(CONTRAST_KEY);
  if (v === null) {
    // No stored choice — honour the OS-level request (WCAG: respect
    // user agent / platform accessibility settings by default).
    return typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-contrast: more)').matches;
  }
  return v === 'true';
}

// Called from index.js BEFORE first render so the page never flashes
// small text and then jumps (also lets non-React chrome inherit it).
export function applyRootFontSize(scale) {
  document.documentElement.style.fontSize = TEXT_SCALES[scale] || '100%';
}

const DisplaySettingsContext = createContext(null);

export function DisplaySettingsProvider({ children }) {
  const [textScale, setTextScaleState] = useState(readStoredScale);
  const [highContrast, setHighContrastState] = useState(readStoredContrast);

  useEffect(() => {
    applyRootFontSize(textScale);
  }, [textScale]);

  const setTextScale = (scale) => {
    const next = TEXT_SCALES[scale] ? scale : 'md';
    localStorage.setItem(SCALE_KEY, next);
    setTextScaleState(next);
  };

  const setHighContrast = (on) => {
    localStorage.setItem(CONTRAST_KEY, String(!!on));
    setHighContrastState(!!on);
  };

  const value = useMemo(
    () => ({ textScale, setTextScale, highContrast, setHighContrast }),
    [textScale, highContrast]
  );

  return (
    <DisplaySettingsContext.Provider value={value}>
      {children}
    </DisplaySettingsContext.Provider>
  );
}

export function useDisplaySettings() {
  const ctx = useContext(DisplaySettingsContext);
  if (!ctx) {
    // Render paths outside the provider (tests, storybook) get inert
    // defaults instead of a crash.
    return {
      textScale: 'md',
      setTextScale: () => {},
      highContrast: false,
      setHighContrast: () => {},
    };
  }
  return ctx;
}

// Drop-in replacement for `<ChakraProvider theme={theme}>` that reacts to
// the high-contrast toggle. Used at every ChakraProvider mount point so
// nested providers (App.js has two) stay in sync.
export function DisplayChakraProvider({ children }) {
  const { highContrast } = useDisplaySettings();
  const theme = useMemo(() => makeTheme({ highContrast }), [highContrast]);
  return <ChakraProvider theme={theme}>{children}</ChakraProvider>;
}
